import { ActionContext } from "vuex";
import loanService from "@/services/loanService";
import {
  LoanCreateRequest,
  LoanCreateResponse,
  LoanDelinquency,
  LoanDetail,
  LoanListParams,
  LoanPaymentRequest,
  LoanPaymentResponse,
  LoanListItem,
  CustomerListParams,
  CustomerListItem,
  LoanCalculateRequest,
  PaymentMovementDto,
  PaymentMovementReportDto,
  UpcomingPaymentsReportDto,
  OverdueLoansReportDto,
  PaymentMovementsParams,
  PagedResult,
} from "@/models/loan.model";

interface State {
  loans: LoanListItem[];
  total: number;
  customers: CustomerListItem[];
  customersTotal: number;
  currentLoan: LoanDetail | null;
  delinquency: LoanDelinquency | null;
  preview: LoanCreateResponse | null;
}

type Ctx = ActionContext<State, any>;

const state: State = {
  loans: [],
  total: 0,
  customers: [],
  customersTotal: 0,
  currentLoan: null,
  delinquency: null,
  preview: null,
};

const mutations = {
  setLoans(state: State, payload: { items: LoanListItem[]; total: number }) {
    state.loans = payload.items;
    state.total = payload.total;
  },
  setCustomers(state: State, payload: { items: CustomerListItem[]; total: number }) {
    state.customers = payload.items;
    state.customersTotal = payload.total;
  },
  setCurrentLoan(state: State, payload: LoanDetail | null) {
    state.currentLoan = payload;
  },
  setDelinquency(state: State, payload: LoanDelinquency | null) {
    state.delinquency = payload;
  },
  setPreview(state: State, payload: LoanCreateResponse | null) {
    state.preview = payload;
  },
};

const actions = {
  async loadLoans({ commit, dispatch }: Ctx, params: LoanListParams = {}) {
    try {
      const response = await loanService.listLoans(params);
      commit("setLoans", response);
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async loadCustomers({ commit, dispatch }: Ctx, params: CustomerListParams = {}) {
    try {
      const response = await loanService.listCustomers(params);
      commit("setCustomers", response);
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async calculateLoan({ commit, dispatch }: Ctx, payload: LoanCalculateRequest): Promise<LoanCreateResponse> {
    try {
      const response = await loanService.calculateLoan(payload);
      commit("setPreview", response);
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      commit("setPreview", null);
      throw error;
    }
  },

  async createLoan({ dispatch }: Ctx, payload: LoanCreateRequest): Promise<LoanCreateResponse> {
    try {
      const response = await loanService.createLoan(payload);
      dispatch("notificator/success", "save-success", { root: true });
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async fetchLoan({ commit, dispatch }: Ctx, loanId: string) {
    try {
      const loan = await loanService.getLoanById(loanId);
      commit("setCurrentLoan", loan);
      return loan;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      commit("setCurrentLoan", null);
      throw error;
    }
  },

  async deleteLoan({ dispatch }: Ctx, loanId: string) {
    try {
      await loanService.deleteLoan(loanId);
      dispatch("notificator/success", "delete-success", { root: true });
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async closeLoan({ dispatch }: Ctx, payload: { loanId: string; reason: string }) {
    try {
      await loanService.closeLoan(payload.loanId, payload.reason);
      dispatch("notificator/success", "loan-close-success", { root: true });
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async addPayment({ dispatch }: Ctx, payload: { loanId: string; payment: LoanPaymentRequest }): Promise<LoanPaymentResponse> {
    try {
      const response = await loanService.addPayment(payload.loanId, payload.payment);
      dispatch("notificator/success", "save-success", { root: true });
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async updatePayment({ dispatch }: Ctx, payload: { loanId: string; paymentId: string; payment: LoanPaymentRequest }): Promise<LoanPaymentResponse> {
    try {
      const response = await loanService.updatePayment(payload.loanId, payload.paymentId, payload.payment);
      dispatch("notificator/success", "save-success", { root: true });
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async fetchDelinquency({ commit, dispatch }: Ctx, payload: { loanId: string; asOf: string }) {
    try {
      const response = await loanService.getDelinquency(payload.loanId, payload.asOf);
      commit("setDelinquency", response);
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      commit("setDelinquency", null);
      throw error;
    }
  },

  async fetchPaymentMovements({ dispatch }: Ctx, payload: { loanId: string; paymentId: string }): Promise<PaymentMovementDto[]> {
    try {
      return await loanService.getPaymentMovements(payload.loanId, payload.paymentId);
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async fetchLoanMovements({ dispatch }: Ctx, payload: { loanId: string; params?: PaymentMovementsParams }): Promise<PagedResult<PaymentMovementDto>> {
    try {
      return await loanService.getLoanMovements(payload.loanId, payload.params);
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async fetchMovementsReport({ dispatch }: Ctx, params: { fromDate?: string; toDate?: string; dealerId?: string; garageId?: string }): Promise<PaymentMovementReportDto> {
    try {
      return await loanService.getMovementsReport(params);
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async fetchUpcomingPayments({ dispatch }: Ctx, params: { daysAhead?: number; dealerId?: string; garageId?: string } = {}): Promise<UpcomingPaymentsReportDto> {
    try {
      return await loanService.getUpcomingPayments(params);
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async fetchOverdueReport({ dispatch }: Ctx, params: { asOf?: string; garageId?: string } = {}): Promise<OverdueLoansReportDto> {
    try {
      return await loanService.getOverdueReport(params);
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },
};

const getters = {
  loans: (state: State) => state.loans,
  total: (state: State) => state.total,
  customers: (state: State) => state.customers,
  customersTotal: (state: State) => state.customersTotal,
  currentLoan: (state: State) => state.currentLoan,
  delinquency: (state: State) => state.delinquency,
  preview: (state: State) => state.preview,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
